/**
 * Category landing — `#/category/<slug>` (first used for Change Outfit).
 *
 * Hero (title, blurb, optional art) + search + style chips + 2-column grid of
 * that category's templates. Each card opens the normal template detail, so
 * there is no new editor flow.
 *
 * Style chips are DATA: they come from `Template.tags` (CMS-managed), plus
 * "Trending" (hot badge) and Video / Photo (only when both kinds exist). A tag chip only appears when at least one template
 * carries it — nothing is hard-coded to look populated.
 */
import { api } from '../api/client.js';
import { navigate } from '../router.js';
import { icon } from '../components/icon.js';
import { escapeHtml } from '../components/placeholder.js';
import {
  bindCardImages,
  templateCardHtml,
  templateCardSkeletonHtml,
} from '../components/templateCard.js';
import { toastApiError } from '../ui/toast.js';
import type { Category, Template } from '@loomia/shared';

interface Copy {
  blurb: string;
  /** Hero artwork (static asset), shown only when present. */
  art?: string;
  searchHint: string;
}

const COPY: Record<string, Copy> = {
  'change-outfit': {
    blurb: 'Try on different styles and transform your look with AI. From casual to fashion, wedding to fantasy.',
    art: '/img/outfit/hero.webp',
    searchHint: 'Search outfits, styles, themes…',
  },
};
const FALLBACK_COPY: Copy = { blurb: 'Pick a template to get started.', searchHint: 'Search templates…' };

const TAG_ICONS: Record<string, string> = {
  casual: 'apparel',
  formal: 'checkroom',
  wedding: 'diamond',
  fantasy: 'auto_fix_high',
  traditional: 'account_balance',
  seasonal: 'ac_unit',
  couple: 'favorite',
};
const TAG_ORDER = Object.keys(TAG_ICONS);

interface Chip {
  id: string;
  label: string;
  icon: string;
  match: (t: Template) => boolean;
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function buildChips(templates: Template[]): Chip[] {
  const chips: Chip[] = [{ id: 'all', label: 'All', icon: 'grid_view', match: () => true }];
  if (templates.some((t) => t.badges.includes('hot'))) {
    chips.push({
      id: 'trending',
      label: 'Trending',
      icon: 'local_fire_department',
      match: (t) => t.badges.includes('hot'),
    });
  }
  // Video / Photo only when the category actually has both kinds.
  if (templates.some((t) => t.mediaKind === 'video') && templates.some((t) => t.mediaKind === 'image')) {
    chips.push(
      { id: 'video', label: 'Video', icon: 'play_circle', match: (t) => t.mediaKind === 'video' },
      { id: 'photo', label: 'Photo', icon: 'image', match: (t) => t.mediaKind === 'image' },
    );
  }
  const seen = new Set<string>();
  for (const t of templates) for (const g of t.tags ?? []) seen.add(g);
  const rank = (g: string): number => {
    const i = TAG_ORDER.indexOf(g);
    return i === -1 ? TAG_ORDER.length : i;
  };
  [...seen]
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .forEach((g) =>
      chips.push({
        id: `tag:${g}`,
        label: titleCase(g),
        icon: TAG_ICONS[g] ?? 'auto_awesome',
        match: (t) => (t.tags ?? []).includes(g),
      }),
    );
  return chips;
}

function goBack(): void {
  if (window.history.length > 1) window.history.back();
  else navigate('/home');
}

function titleHtml(title: string): string {
  const words = title.trim().split(/\s+/);
  if (words.length < 2) return `<span class="cat-grad">${escapeHtml(title)}</span>`;
  const last = words.pop()!;
  return `${escapeHtml(words.join(' '))} <span class="cat-grad">${escapeHtml(last)}</span>`;
}

export function renderCategory(root: HTMLElement, slug: string): void {
  let alive = true;
  let chips: Chip[] = [{ id: 'all', label: 'All', icon: 'grid_view', match: () => true }];
  let templates: Template[] = [];
  let category: Category | null = null;
  const state = { q: '', chip: 'all' };
  const copy = COPY[slug] ?? FALLBACK_COPY;
  const guessTitle = slug
    .split('-')
    .map(titleCase)
    .join(' ');

  root.innerHTML = `
    <div class="cat explore">
      <button class="cat-back" type="button" data-action="back" aria-label="Back">${icon('arrow_back')}</button>
      <header class="cat-hero${copy.art ? ' has-art' : ''}">
        <div class="cat-hero-text">
          <h1 class="cat-title" id="cat-title">${titleHtml(guessTitle)}</h1>
          <p class="cat-blurb">${escapeHtml(copy.blurb)}</p>
        </div>
        ${copy.art ? `<img class="cat-art" src="${escapeHtml(copy.art)}" alt="" decoding="async" fetchpriority="high" />` : ''}
      </header>

      <div class="ex-search">
        <span class="ex-search-ic">${icon('search')}</span>
        <input id="cat-q" type="search" inputmode="search" enterkeyhint="search" autocomplete="off"
          placeholder="${escapeHtml(copy.searchHint)}" aria-label="Search" />
        <button class="ex-clear" id="cat-clear" type="button" aria-label="Clear search" hidden>${icon('close')}</button>
      </div>

      <div class="ex-chips" id="cat-chips" role="tablist" aria-label="Styles"></div>
      <p class="ex-count" id="cat-count" aria-live="polite"></p>
      <div class="tpl-grid" id="cat-grid">${templateCardSkeletonHtml(6)}</div>
    </div>`;

  const q = root.querySelector<HTMLInputElement>('#cat-q')!;
  const clear = root.querySelector<HTMLButtonElement>('#cat-clear')!;
  const chipsEl = root.querySelector<HTMLElement>('#cat-chips')!;
  const grid = root.querySelector<HTMLElement>('#cat-grid')!;
  const count = root.querySelector<HTMLElement>('#cat-count')!;

  const paintChips = (): void => {
    chipsEl.hidden = chips.length <= 1;
    chipsEl.innerHTML = chips
      .map(
        (c) => `<button class="ex-chip${c.id === state.chip ? ' is-active' : ''}" type="button" role="tab"
          aria-selected="${c.id === state.chip}" data-chip="${escapeHtml(c.id)}">${icon(c.icon, { fill: c.id === state.chip })}${escapeHtml(c.label)}</button>`,
      )
      .join('');
  };

  const paintGrid = (): void => {
    const term = state.q.trim().toLowerCase();
    const chip = chips.find((c) => c.id === state.chip) ?? chips[0]!;
    const hits = templates.filter(
      (t) =>
        chip.match(t) &&
        (term === '' || `${t.title} ${t.description} ${(t.tags ?? []).join(' ')}`.toLowerCase().includes(term)),
    );
    clear.hidden = state.q === '';
    count.textContent = `${hits.length} ${hits.length === 1 ? 'style' : 'styles'}`;
    if (hits.length === 0) {
      grid.className = '';
      grid.innerHTML = `
        <div class="empty">
          <div class="empty-icon">${icon('search')}</div>
          <p class="muted">${templates.length === 0 ? 'No templates here yet.' : 'No styles match.'}</p>
          ${templates.length === 0 ? '' : '<button class="btn btn-secondary" id="cat-reset" type="button">Reset filters</button>'}
        </div>`;
      return;
    }
    grid.className = 'tpl-grid';
    grid.innerHTML = hits.map((t) => templateCardHtml(t, { explore: true })).join('');
    bindCardImages(grid);
  };

  let debounce: number | undefined;
  q.addEventListener('input', () => {
    state.q = q.value;
    window.clearTimeout(debounce);
    debounce = window.setTimeout(paintGrid, 120);
  });
  q.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') q.blur();
  });
  root.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-action="back"]')) {
      goBack();
      return;
    }
    const chipBtn = t.closest<HTMLElement>('[data-chip]');
    if (chipBtn?.dataset.chip) {
      state.chip = chipBtn.dataset.chip;
      paintChips();
      paintGrid();
    } else if (t.closest('#cat-clear')) {
      state.q = '';
      q.value = '';
      paintGrid();
      q.focus();
    } else if (t.closest('#cat-reset')) {
      state.q = '';
      state.chip = 'all';
      q.value = '';
      paintChips();
      paintGrid();
    } else if (t.closest('#cat-retry')) {
      void load();
    }
  });

  const load = async (): Promise<void> => {
    try {
      const cats = await api.listCategories();
      category = cats.find((c) => c.slug === slug) ?? null;
      if (!category) {
        if (!alive) return;
        grid.className = '';
        grid.innerHTML = `
          <div class="empty">
            <div class="empty-icon">${icon('search')}</div>
            <p class="muted">We couldn’t find that category.</p>
            <a class="btn btn-secondary" href="#/explore">Explore templates</a>
          </div>`;
        return;
      }
      const all: Template[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < 10; i++) {
        const page = await api.listTemplates({ category: category.id, limit: 100, ...(cursor ? { cursor } : {}) });
        all.push(...page.templates);
        if (!page.nextCursor) break;
        cursor = page.nextCursor;
      }
      if (!alive) return;
      templates = all;
      chips = buildChips(templates);
      const titleEl = root.querySelector<HTMLElement>('#cat-title');
      if (titleEl) titleEl.innerHTML = titleHtml(category.title);
      paintChips();
      paintGrid();
    } catch (err) {
      if (!alive) return;
      toastApiError(err);
      grid.className = '';
      grid.innerHTML = `
        <div class="empty">
          <div class="empty-icon">${icon('cloud_off')}</div>
          <p class="muted">Couldn’t load templates.</p>
          <button class="btn btn-secondary" id="cat-retry" type="button">Retry</button>
        </div>`;
    }
  };

  paintChips();
  const mo = new MutationObserver(() => {
    if (!root.isConnected) {
      alive = false;
      mo.disconnect();
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
  void load();
}
